import React, { useState, useCallback, useMemo, Fragment } from 'react';
import { FormattedMessage, injectIntl, IntlFormatters } from 'react-intl';
import './modal.less';
import { Modal, Form, Button } from 'antd';
import { withRouter } from 'react-router-dom';
import { http_request_new, response_type } from '../../http-worker/http-func';
import { useDeepCompareEffect, useDeepCompareMemo } from 'use-deep-compare';
import TextArea from 'antd/lib/input/TextArea';
import { operation_type, props_type, request_content_type, validate_type } from './interface';

/**
 * Description 輸入框
 * @param {({ description?: string; set_description?: React.Dispatch<React.SetStateAction<string | undefined>> })} props 傳入的參數
 */
export const description_input = (props: { description?: string; set_description?: React.Dispatch<React.SetStateAction<string | undefined>> }) => (
  <Form.Item key='description' name='description' label={<FormattedMessage id='description' />}>
    <TextArea
      rows={4}
      value={props.description}
      onChange={({ target }: React.ChangeEvent<HTMLTextAreaElement>) => {
        try {
          props.set_description?.(target.value);
        } catch (err) {
          console.error(err);
        }
      }}
    />
  </Form.Item>
);

export const set_status = (
  validator: () => void,
  format: string,
  set_validators: React.Dispatch<React.SetStateAction<validate_type | undefined>>,
  formatMessage: IntlFormatters['formatMessage'],
  set_special_field_error: React.Dispatch<React.SetStateAction<boolean | undefined>>
) => {
  try {
    validator();
    set_validators((current_state: validate_type | undefined) => ({
      ...current_state,
      code: { status: 'success', msg: undefined }
    }));
    set_special_field_error(false);
  } catch (err) {
    set_validators((current_state: validate_type | undefined) => ({
      ...current_state,
      code: {
        status: 'error',
        msg: formatMessage({ id: 'format_is_not_valid' }, { format: format.toUpperCase() })
      }
    }));
    set_special_field_error(true);
  }
};

const TableListModal: React.FC<props_type> = (props: props_type) => {
  const {
    visible,
    selectable,
    operations,
    action,
    action_id,
    filter_operations,
    set_selectable,
    set_visible,
    set_action,
    set_modal_title,
    set_item_selected,
    result_to_do,
    set_process_status
  }: props_type = useMemo(() => props, [props]);

  const [disabled, set_disabled]: [boolean, React.Dispatch<React.SetStateAction<boolean>>] = useState<boolean>(false);
  const [http_result, set_http_result]: [any, React.Dispatch<React.SetStateAction<any>>] = useState<any>();
  const [processing, set_processing]: [0 | 1, React.Dispatch<React.SetStateAction<0 | 1>>] = useState<0 | 1>(0);

  const [special_field_error, set_special_field_error]: [boolean | undefined, React.Dispatch<React.SetStateAction<boolean | undefined>>] = useState<
    boolean | undefined
  >();

  const [tick, set_tick]: [any, React.Dispatch<React.SetStateAction<any>>] = useState<any>();
  const { formatMessage } = useMemo(() => props.intl, [props]);
  const path = props?.path ? props.path : props.history.location.pathname.substring(1).replace(/(\/create|\/edit)/g, '');
  const position: number = useMemo(() => path.lastIndexOf('/') + 1, [path]);
  const list_type: string = useMemo(() => path.substr(position, path.length - position).replace(/-/gi, ''), [path, position]);

  const [validators, set_validators]: [validate_type | undefined, React.Dispatch<React.SetStateAction<validate_type | undefined>>] = useState<
    validate_type | undefined
  >();

  const operation = useMemo(() => {
    let operations_format: operation_type[] | undefined = filter_operations && filter_operations.length ? filter_operations : operations?.();
    return (action_id !== undefined && operations_format?.[action_id]) || ({} as operation_type);
  }, [action_id, operations, filter_operations]);

  const [form] = Form.useForm();

  /** 彈出式視窗按 OK 按鈕時的動作 */
  const handle_ok: () => void = useCallback(async (): Promise<void> => {
    if (action !== undefined) {
      const request_content = operation.handle_ok?.({ props, form }) as request_content_type;
      if (request_content)
        try {
          await form.validateFields();
          set_processing(1);
          set_process_status?.(1);
          const http_result = await http_request_new({
            key: request_content.key ?? list_type,
            props: props,
            func: operation.refresh_set_state ? operation.refresh_set_state(props) : undefined,
            url: request_content.url,
            method: request_content.hasOwnProperty('own_method') ? (request_content.own_method as any) : [1, request_content.patch ? 4 : 2, 3][action],
            body: request_content.body,
            post_process: operation.refresh_post_process?.(props),
            refresh_url: operation.refresh_url?.(props),
            no_refresh: operation.no_refresh,
            content_type: request_content.content_type,
            custom_warning: request_content.custom_warning
          });
          if (((http_result as response_type)?.code || 0) < 400 || action === 2) {
            if (!operation?.modal_close) {
              set_visible && set_visible(undefined);
              set_modal_title && set_modal_title('');
              set_action && set_action(undefined);
            }
            set_http_result(http_result);
            set_processing(0);
            set_process_status?.(2);
            set_disabled(false);
            result_to_do?.({ http_result, props });
          } else {
            set_http_result(undefined);
            set_processing(0);
            set_process_status?.(3);
            set_disabled(false);
          }
        } catch (errorInfo) {
          console.error('Failed:', errorInfo);
          set_processing(0);
          set_process_status?.(3);
          set_disabled(false);
        }
    } else set_visible && set_visible(undefined);
  }, [action, list_type, props, operation, set_action, set_modal_title, set_visible, form, result_to_do, set_process_status]);

  //监听enter事件
  // useDeepCompareEffect(() => {
  //   const dom_type: [string, string[]][] = Object?.entries?.({
  //     class: ['react-codemirror2'],
  //     ele: ['textarea']
  //   })
  //   let eles: Element[] =[];
  //   for (let i = 0; i < dom_type?.length; i++) {
  //     if (dom_type?.[i]?.[0] === 'class') {
  //       dom_type?.[i]?.[1]?.map?.((dom: string) => {
  //         eles?.push?.(...document.getElementsByClassName?.(dom));
  //         return '';
  //       })
  //     } else {
  //       dom_type?.[i]?.[1]?.map?.((dom: string) => {
  //         eles?.push?.(...document.getElementsByTagName?.(dom));
  //         return '';
  //       })
  //     }
  //   }
  //   const prevent = (e: KeyboardEvent) => {
  //     let event = e || window.event;
  //     let code = event.keyCode || event.which || event.charCode;
  //     if (code === 13) {
  //       e?.stopPropagation?.();
  //     }
  //   };
  //   for (let i = 0; i < eles?.length; i++) {
  //     (eles?.[i] as HTMLElement)?.addEventListener?.('keyup', prevent);
  //   }
  //   const key_up_func = (e: KeyboardEvent) => {
  //     let event = e || window.event;
  //     let code = event.keyCode || event.which || event.charCode;
  //     if (code === 13 && !disabled) {
  //       handle_ok?.();
  //     }
  //   };
  //   window?.addEventListener?.('keyup', key_up_func);
  //   return () => {
  //     window?.removeEventListener?.('keyup', key_up_func);
  //     for (let i = 0; i < eles?.length; i++) {
  //       (eles?.[i] as HTMLElement)?.removeEventListener?.('keyup', prevent);
  //     }
  //   };
  // }, [disabled]);

  // 詢問視窗按取消
  const handle_cancel: () => void = useCallback((): void => {
    setTimeout(() => {
      set_visible && set_visible(undefined);
    }, 0);
    set_modal_title && set_modal_title('');
    !selectable && set_action && set_action(undefined);
    setTimeout(() => {
      set_item_selected && set_item_selected(undefined);
    }, 0);
    set_selectable && set_selectable(0);
  }, [selectable, set_action, set_item_selected, set_modal_title, set_visible, set_selectable]);

  //初始化
  useDeepCompareEffect((): void => {
    form && operation.initial_action && operation.initial_action({ props, form, action, set_tick, set_disabled });
  }, [tick]);

  const modal_message_new: JSX.Element | false | undefined | any = useDeepCompareMemo(
    () =>
      !!visible &&
      action_id !== undefined &&
      operation.modal_message({
        props,
        set_tick,
        set_item_selected,
        formatMessage,
        form,
        validators,
        set_validators,
        set_special_field_error,
        operation,
        type: list_type,
        http_result,
        special_field_error,
        set_disabled,
        disabled
      }),
    [
      props,
      set_tick,
      set_item_selected,
      formatMessage,
      form,
      operation,
      action_id,
      visible,
      validators,
      set_validators,
      set_special_field_error,
      list_type,
      http_result,
      special_field_error,
      form?.getFieldsValue(),
      set_disabled
    ]
  );

  useDeepCompareEffect((): void => {
    if (visible) {
      set_disabled(action !== 2 && typeof modal_message_new?.type !== 'string');
      form && operation.initial_action && operation.initial_action({ props, form, action, set_tick, set_disabled });
    } else {
      props.set_action?.(undefined);
      props.set_action_id?.(undefined);
    }
  }, [visible, action, form, operation.initial_action, props.item_selected, set_tick, modal_message_new?.type]);

  return (
    <Modal
      width={operation.width || 660}
      bodyStyle={path.includes('configs') && action === 0 ? { paddingTop: 10, lineHeight: 0, height: '100%' } : {}}
      title={
        <Fragment>
          {operation.modal_title && <FormattedMessage id={operation.modal_title} values={operation.modal_title_values} />}
          {operation.modal_title_el && operation.modal_title_el({ props })}
        </Fragment>
      }
      visible={!!visible}
      destroyOnClose={true}
      maskClosable={false}
      keyboard={true}
      onCancel={handle_cancel}
      footer={[
        <Button key='cancel' onClick={handle_cancel}>
          {formatMessage({ id: 'cancel' })}
        </Button>,
        <Button key='ok' type='primary' loading={!!processing} onClick={handle_ok} disabled={disabled}>
          {formatMessage({ id: 'ok' })}
        </Button>
      ]}
      {...(operation.no_footer ? { footer: null } : operation.footer ? { footer: operation.footer({ props, formatMessage, form, handle_cancel }) } : {})}
    >
      {modal_message_new}
    </Modal>
  );
};

export default injectIntl(withRouter(TableListModal));
